
import {
    UserModel,
    CountryModel,
    ChallengeModel,
    ContactModel,
    NeedModel,
    PropertyModel,
    MandateModel,
    MatchingModel,
    PublicationModel,
    PropertyMediaFileModel,
    TransactionModel,
    UserSessionModel,
    MediaFileModel,
    ActivityModel,
    TaskModel,
    ActivityMediaFileModel,
    PlanningModel,
    BirthWishModel,
    DocumentModel,
    PermissionModel,
    LeaseModel,
    AgentTargetModel
} from '@/Domains';

import { makeEntityService } from './makeEntity-service';
import { DOMAIN_TYPES } from '@/Shared';

/**
 * =====================================================
 * Domain Entity Services
 * =====================================================
 *
 * Provides a set of reusable service objects for CRUD operations
 * on domain entities. Each service is created via the generic
 * `makeEntityService` factory and is bound to a specific REST endpoint.
 *
 * Responsibilities:
 * - Encapsulate HTTP or API interactions for a given entity
 * - Provide a consistent interface across all domain entities
 * - Decouple the domain logic from networking or persistence details
 *
 * Author: Samano CASTRE
 * Date: January 2026
 */
const EXPORT_PREFIX = "/export";

/**
 * -----------------------------------------------------
 * Challenge domain service
 * -----------------------------------------------------
 */
export const ChallengeService = makeEntityService<ChallengeModel>('/challenges', EXPORT_PREFIX , DOMAIN_TYPES.Challenge);


/**
 * -----------------------------------------------------
 * Contact domain service
 * -----------------------------------------------------
 */
export const ContactService = makeEntityService<ContactModel>('/contacts', EXPORT_PREFIX , DOMAIN_TYPES.Contact);


/**
 * -----------------------------------------------------
 * User domain service
 * -----------------------------------------------------
 */
export const UserService = makeEntityService<UserModel>('/users', EXPORT_PREFIX , DOMAIN_TYPES.User);


/**
 * -----------------------------------------------------
 * UserSession domain service
 * -----------------------------------------------------
 */
export const UserSessionService = makeEntityService<UserSessionModel>('/user-sessions');


/**
 * -----------------------------------------------------
 * Need domain service
 * -----------------------------------------------------
 */
export const NeedService = makeEntityService<NeedModel>('/needs', EXPORT_PREFIX , DOMAIN_TYPES.Need);


/**
 * -----------------------------------------------------
 * Property domain service
 * -----------------------------------------------------
 */
export const PropertyService = makeEntityService<PropertyModel>('/properties', EXPORT_PREFIX , DOMAIN_TYPES.Property);


/**
 * -----------------------------------------------------
 * Mandate domain service
 * -----------------------------------------------------
 */
export const MandateService = makeEntityService<MandateModel>('/mandates', EXPORT_PREFIX , DOMAIN_TYPES.Mandate);


/**
 * -----------------------------------------------------
 * Transaction domain service
 * -----------------------------------------------------
 */
export const TransactionService = makeEntityService<TransactionModel>('/transactions', EXPORT_PREFIX , DOMAIN_TYPES.Transaction);


/**
 * -----------------------------------------------------
 * Lease domain service
 * -----------------------------------------------------
 */
export const LeaseService = makeEntityService<LeaseModel>('/leases', EXPORT_PREFIX , DOMAIN_TYPES.Lease);


/**
 * -----------------------------------------------------
 * Matching domain service
 * -----------------------------------------------------
 */
export const MatchingService = makeEntityService<MatchingModel>('/matchings', EXPORT_PREFIX , DOMAIN_TYPES.Matching);


/**
 * -----------------------------------------------------
 * Country domain service
 * -----------------------------------------------------
 */
export const CountryService = makeEntityService<CountryModel>('/countries');


/**
 * -----------------------------------------------------
 * Publication domain service
 * -----------------------------------------------------
 */
export const PublicationService = makeEntityService<PublicationModel>('/publications', EXPORT_PREFIX , DOMAIN_TYPES.Publication);

/**
 * -----------------------------------------------------
 * AgentTarget domain service
 * -----------------------------------------------------
 */
export const AgentTargetService = makeEntityService<AgentTargetModel>('/agent-targets', EXPORT_PREFIX , DOMAIN_TYPES.AgentTarget);



/**
 * -----------------------------------------------------
 * PropertyMedia domain service
 * -----------------------------------------------------
 */
export const PropertyMediaService = makeEntityService<PropertyMediaFileModel>('/properties/medias');


/**
 * -----------------------------------------------------
 * MediaFile domain service
 * -----------------------------------------------------
 */
export const MediaFileService = makeEntityService<MediaFileModel>('/media-files');


/**
 * -----------------------------------------------------
 * Task domain service
 * -----------------------------------------------------
 */
export const TaskService = makeEntityService<TaskModel>('/tasks', EXPORT_PREFIX , DOMAIN_TYPES.Task);


/**
 * -----------------------------------------------------
 * BirthWish domain service
 * -----------------------------------------------------
 */
export const BirthWishService = makeEntityService<BirthWishModel>('/birth-wishes', EXPORT_PREFIX , DOMAIN_TYPES.BirthWish);


/**
 * -----------------------------------------------------
 * Document domain service
 * -----------------------------------------------------
 */
export const DocumentService = makeEntityService<DocumentModel>('/documents', EXPORT_PREFIX , DOMAIN_TYPES.Document);


/**
 * -----------------------------------------------------
 * Permission domain service
 * -----------------------------------------------------
 */
export const PermissionService = makeEntityService<PermissionModel>('/permissions', EXPORT_PREFIX , DOMAIN_TYPES.Permission);


/**
 * -----------------------------------------------------
 * Activity domain service
 * -----------------------------------------------------
 */
export const ActivityService = makeEntityService<ActivityModel>('/activities', EXPORT_PREFIX , DOMAIN_TYPES.Activity);


/**
 * -----------------------------------------------------
 * ActivityMedia domain service
 * -----------------------------------------------------
 */
export const ActivityMediaService = makeEntityService<ActivityMediaFileModel>('/activities/medias');


/**
 * -----------------------------------------------------
 * Planning domain service
 * -----------------------------------------------------
 */
export const PlanningService = makeEntityService<PlanningModel>('/plannings', EXPORT_PREFIX , DOMAIN_TYPES.Planning);